import { DEFAULT_VIDEO_CONSTRAINTS, SELECTED_AUDIO_INPUT_KEY, SELECTED_VIDEO_INPUT_KEY } from '@uplift/constants';
import { localStorage } from '@uplift/services';
import { useCallback, useState } from 'react';
import Video, { LocalVideoTrack, LocalAudioTrack, CreateLocalTrackOptions } from 'twilio-video';

async function getDeviceInfo() {
  const devices = await navigator.mediaDevices.enumerateDevices();

  return {
    audioInputDevices: devices.filter(device => device.kind === 'audioinput'),
    videoInputDevices: devices.filter(device => device.kind === 'videoinput'),
    audioOutputDevices: devices.filter(device => device.kind === 'audiooutput'),
    hasAudioInputDevices: devices.some(device => device.kind === 'audioinput'),
    hasVideoInputDevices: devices.some(device => device.kind === 'videoinput'),
  };
}

// This function will return 'true' when the specified permission has been denied by the user.
// If the API doesn't exist, or the query function returns an error, 'false' will be returned.
// Does not exist in safari

async function isPermissionDenied(name: PermissionName) {
  if (navigator.permissions) {
    try {
      const result = await navigator.permissions.query({ name });

      return result.state === 'denied';
    } catch {
      return false;
    }
  } else {
    return false;
  }
}


// eslint-disable-next-line import/no-default-export
export default function useLocalTracks() {
  const [ audioTrack, setAudioTrack ] = useState<LocalAudioTrack>();
  const [ videoTrack, setVideoTrack ] = useState<LocalVideoTrack>();
  const [ isAcquiringLocalTracks, setIsAcquiringLocalTracks ] = useState(false);

  const getLocalAudioTrack = useCallback((deviceId?: string) => {
    const options: CreateLocalTrackOptions = {};

    if (deviceId) {
      options.deviceId = { exact: deviceId };
    }

    return Video.createLocalAudioTrack(options).then(newTrack => {
      setAudioTrack(newTrack);
      return newTrack;
    });
  }, []);

  const getLocalVideoTrack = useCallback(async () => {
    const { videoInputDevices } = await getDeviceInfo();
    const selectedVideoDeviceId = localStorage.getItem(SELECTED_VIDEO_INPUT_KEY);

    const hasSelectedVideoDevice = videoInputDevices.some(
      device => selectedVideoDeviceId && device.deviceId === selectedVideoDeviceId
    );

    const options: CreateLocalTrackOptions = {
      ...(DEFAULT_VIDEO_CONSTRAINTS as MediaTrackConstraints),
      name: `camera-${Date.now()}`,
      ...(hasSelectedVideoDevice && { deviceId: { exact: selectedVideoDeviceId! } }),
      // Interestingly not a default
      workaroundWebKitBug180748: true
    };

    return Video.createLocalVideoTrack(options).then(newTrack => {
      setVideoTrack(newTrack);
      return newTrack;
    });
  }, []);

  const removeLocalAudioTrack = useCallback(() => {
    if (audioTrack) {
      audioTrack.stop();
      setAudioTrack(undefined);
    }
  }, [ audioTrack ]);

  const removeLocalVideoTrack = useCallback(() => {
    if (videoTrack) {
      videoTrack.stop();
      setVideoTrack(undefined);
    }
  }, [ videoTrack ]);

  const getAudioAndVideoTracks = useCallback(async () => {

    const { audioInputDevices, videoInputDevices, hasAudioInputDevices, hasVideoInputDevices } = await getDeviceInfo();

    if (!hasAudioInputDevices && !hasVideoInputDevices) return Promise.resolve();
    if (isAcquiringLocalTracks || audioTrack || videoTrack) return Promise.resolve();

    setIsAcquiringLocalTracks(true);

    const selectedAudioDeviceId = localStorage.getItem(SELECTED_AUDIO_INPUT_KEY) || 'default';
    const selectedVideoDeviceId = localStorage.getItem(SELECTED_VIDEO_INPUT_KEY) || 'default';

    const hasSelectedAudioDevice = audioInputDevices.some(
      device => selectedAudioDeviceId && device.deviceId === selectedAudioDeviceId
    );
    const hasSelectedVideoDevice = videoInputDevices.some(
      device => selectedVideoDeviceId && device.deviceId === selectedVideoDeviceId
    );

    // In Chrome, it is possible to deny permissions to only audio or only video.
    // If that has happened, then we don't want to attempt to acquire the device.
    // The values camera and microphone are not recognized as part of PermissionName in TS - even though
    // they are valid and part of the native api - thus why they are forced as the type.
    const isCameraPermissionDenied = await isPermissionDenied('camera' as PermissionName);
    const isMicrophonePermissionDenied = await isPermissionDenied('microphone' as PermissionName);

    const shouldAcquireVideo = hasVideoInputDevices && !isCameraPermissionDenied;
    const shouldAcquireAudio = hasAudioInputDevices && !isMicrophonePermissionDenied;


    const localTrackConstraints = {
      video: shouldAcquireVideo && {
        ...(DEFAULT_VIDEO_CONSTRAINTS as MediaTrackConstraints),
        name: `camera-${Date.now()}`,
        ...(hasSelectedVideoDevice && { deviceId: { exact: selectedVideoDeviceId! } }),
      },
      audio: shouldAcquireAudio && (hasSelectedAudioDevice ? { deviceId: { exact: selectedAudioDeviceId! } } : hasAudioInputDevices),

    };

    return Video.createLocalTracks(localTrackConstraints)
      .then(tracks => {
        const newVideoTrack = tracks.find(track => track.kind === 'video') as LocalVideoTrack;
        const newAudioTrack = tracks.find(track => track.kind === 'audio') as LocalAudioTrack;

        if (newVideoTrack) {
          setVideoTrack(newVideoTrack);

          // Save the deviceId so it can be picked up by the VideoInputList component. This only matters
          // in cases where the user's video is disabled.
          localStorage.setItem(
            SELECTED_VIDEO_INPUT_KEY,
            (newVideoTrack).mediaStreamTrack.getSettings().deviceId ?? ''
          );
        }

        if (newAudioTrack) {
          setAudioTrack(newAudioTrack);
        }

        if (isCameraPermissionDenied && isMicrophonePermissionDenied) {
          const error = new Error();

          error.name = 'NotAllowedError';
          throw error;
        }

        if (isCameraPermissionDenied) {
          const error = new Error('CameraPermissionsDenied');

          error.name = 'CameraPermissionsDenied';
          throw error;
        }

        if (isMicrophonePermissionDenied) {
          const error = new Error('MicrophonePermissionsDenied');

          error.name = 'MicrophonePermissionsDenied';
          throw error;
        }
      })
      .finally(() => setIsAcquiringLocalTracks(false));
  }, [
    audioTrack,
    videoTrack,
    isAcquiringLocalTracks,
  ]);

  const localTracks = [ audioTrack, videoTrack ].filter(track => track !== undefined) as (
    | LocalAudioTrack
    | LocalVideoTrack
  )[];

  return {
    localTracks,
    getLocalVideoTrack,
    getLocalAudioTrack,
    isAcquiringLocalTracks,
    removeLocalAudioTrack,
    removeLocalVideoTrack,
    getAudioAndVideoTracks,
  };
}
